/**
 * Battleship Game Logic
 * Handles game state, ship placement, attacks, and win detection
 */

// Constants
const GRID_SIZE = 10;
const SHIPS = [
  { id: 'carrier', name: 'Aircraft Carrier', size: 5 },
  { id: 'battleship', name: 'Battleship', size: 4 },
  { id: 'cruiser', name: 'Cruiser', size: 3 },
  { id: 'submarine', name: 'Submarine', size: 3 },
  { id: 'destroyer', name: 'Destroyer', size: 2 },
];

/**
 * Create empty grid (10x10)
 */
export const createEmptyGrid = () => {
  return Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(null));
};

/**
 * Helper function to ensure grid is an array (Firebase converts arrays to objects)
 */
const ensureGridIsArray = (grid) => {
  if (!grid) return createEmptyGrid();

  // Convert grid to array if needed
  let workingGrid = grid;
  if (!Array.isArray(grid)) {
    // Grid is an object, convert to array
    workingGrid = [];
    for (let i = 0; i < GRID_SIZE; i++) {
      workingGrid[i] = grid[i];
    }
  }

  // Ensure grid has exactly GRID_SIZE rows
  const arrayGrid = [];
  for (let i = 0; i < GRID_SIZE; i++) {
    const row = workingGrid[i];

    // If row is a valid array (regardless of length), process it
    if (Array.isArray(row)) {
      const newRow = [];
      for (let j = 0; j < GRID_SIZE; j++) {
        // Preserve existing values, use null for missing cells
        newRow.push(row[j] !== undefined ? row[j] : null);
      }
      arrayGrid.push(newRow);
    } else if (row && typeof row === 'object') {
      // Row is an object, convert to array
      const newRow = [];
      for (let j = 0; j < GRID_SIZE; j++) {
        newRow.push(row[j] !== undefined ? row[j] : null);
      }
      arrayGrid.push(newRow);
    } else if (row === null || row === undefined) {
      // Only create empty row if explicitly null/undefined
      arrayGrid.push(Array(GRID_SIZE).fill(null));
    } else {
      // For any other unexpected type, log warning
      console.warn('Unexpected row type in grid:', typeof row, row);
      arrayGrid.push(Array(GRID_SIZE).fill(null));
    }
  }

  return arrayGrid;
};

/**
 * Create initial game state
 */
export const createInitialGameState = (hostId, guestId) => {
  // Randomly choose who goes first
  const firstPlayer = Math.random() < 0.5 ? hostId : guestId;

  return {
    phase: 'placement', // 'placement' or 'battle'
    currentTurn: firstPlayer, // Randomly chosen starting player
    hostId,
    guestId,
    hostReady: false,
    guestReady: false,
    hostShips: [],
    guestShips: [],
    hostAttacks: createEmptyGrid(), // Track attacks on guest (null, 'miss', 'hit')
    guestAttacks: createEmptyGrid(), // Track attacks on host (null, 'miss', 'hit')
    isGameOver: false,
    winner: null,
    lastAttack: null,
    hostShipsSunk: [],
    guestShipsSunk: [],
  };
};

/**
 * Validate ship placement
 */
export const isValidPlacement = (ships, ship, row, col, isHorizontal) => {
  // Check bounds
  if (isHorizontal) {
    if (col + ship.size > GRID_SIZE) return false;
  } else {
    if (row + ship.size > GRID_SIZE) return false;
  }

  // Create a grid to check overlaps
  const grid = createEmptyGrid();

  // Mark existing ships
  ships.forEach(placedShip => {
    for (let i = 0; i < placedShip.size; i++) {
      const r = placedShip.isHorizontal ? placedShip.row : placedShip.row + i;
      const c = placedShip.isHorizontal ? placedShip.col + i : placedShip.col;
      grid[r][c] = true;
    }
  });

  // Check if new ship overlaps
  for (let i = 0; i < ship.size; i++) {
    const r = isHorizontal ? row : row + i;
    const c = isHorizontal ? col + i : col;
    if (grid[r][c]) return false;
  }

  return true;
};

/**
 * Place a ship on the grid
 */
export const placeShip = (ships, shipId, row, col, isHorizontal) => {
  const shipTemplate = SHIPS.find(s => s.id === shipId);
  if (!shipTemplate) return ships;

  // Check if ship is already placed (replace it)
  const filteredShips = ships.filter(s => s.id !== shipId);

  // Validate placement
  if (!isValidPlacement(filteredShips, shipTemplate, row, col, isHorizontal)) {
    return ships;
  }

  // Create positions array
  const positions = [];
  for (let i = 0; i < shipTemplate.size; i++) {
    positions.push({
      row: isHorizontal ? row : row + i,
      col: isHorizontal ? col + i : col,
      hit: false,
    });
  }

  const newShip = {
    ...shipTemplate,
    row,
    col,
    isHorizontal,
    positions,
    sunk: false,
  };

  return [...filteredShips, newShip];
};

/**
 * Random ship placement for quick start
 */
export const randomPlaceShips = () => {
  let ships = [];

  for (const shipTemplate of SHIPS) {
    let placed = false;
    let attempts = 0;

    while (!placed && attempts < 100) {
      const isHorizontal = Math.random() < 0.5;
      const row = Math.floor(Math.random() * GRID_SIZE);
      const col = Math.floor(Math.random() * GRID_SIZE);

      const newShips = placeShip(ships, shipTemplate.id, row, col, isHorizontal);

      if (newShips.length > ships.length) {
        ships = newShips;
        placed = true;
      }

      attempts++;
    }
  }

  return ships;
};

/**
 * Check if all ships are placed
 */
export const allShipsPlaced = (ships) => {
  return ships.length === SHIPS.length;
};

/**
 * Process attack
 */
export const processAttack = (gameState, attackingPlayerId, row, col) => {
  const isHost = attackingPlayerId === gameState.hostId;
  // Don't call ensureGridIsArray again - grids are already normalized before this function
  const attackGrid = isHost ? gameState.hostAttacks : gameState.guestAttacks;
  const defendingShipsOriginal = (isHost ? gameState.guestShips : gameState.hostShips) || [];

  // Safety check: ensure attack grid is properly initialized
  if (!attackGrid || !attackGrid[row] || typeof attackGrid[row][col] === 'undefined') {
    console.error('Attack grid is malformed', { attackGrid, row, col, gameState });
    return { ...gameState, error: 'Game state error. Attack grid not initialized.' };
  }

  // Check if already attacked
  if (attackGrid[row][col] !== null) {
    return { ...gameState, error: 'Already attacked this square' };
  }

  // Create deep copy of attack grid to avoid mutations
  const newAttackGrid = attackGrid.map(r => [...r]);

  // Create a deep copy of defending ships to avoid mutating original state
  const defendingShips = defendingShipsOriginal.map(ship => ({
    ...ship,
    positions: ship.positions.map(pos => ({ ...pos })),
  }));

  // Check if hit
  let hit = false;
  let hitShip = null;

  defendingShips.forEach(ship => {
    ship.positions.forEach((pos, idx) => {
      if (pos.row === row && pos.col === col) {
        hit = true;
        hitShip = ship;
        pos.hit = true;
      }
    });
  });

  // Update grid
  newAttackGrid[row][col] = hit ? 'hit' : 'miss';

  // Check if ship is sunk
  let sunkShip = null;
  if (hitShip) {
    const allHit = hitShip.positions.every(p => p.hit);
    if (allHit && !hitShip.sunk) {
      hitShip.sunk = true;
      sunkShip = hitShip;
    }
  }

  // Update sunk ships list
  const currentSunkShips = isHost
    ? (gameState.hostShipsSunk || [])
    : (gameState.guestShipsSunk || []);

  const newSunkShips = [...currentSunkShips];

  if (sunkShip && !newSunkShips.includes(sunkShip.id)) {
    newSunkShips.push(sunkShip.id);
  }

  // Check win condition
  const allShipsSunk = defendingShips.every(ship => ship.sunk);

  // Switch turns
  const nextTurn = gameState.currentTurn === gameState.hostId
    ? gameState.guestId
    : gameState.hostId;

  const newGameState = {
    ...gameState,
    // Always create deep copies to avoid reference issues
    hostAttacks: isHost ? newAttackGrid : gameState.hostAttacks.map(r => [...r]),
    guestAttacks: isHost ? gameState.guestAttacks.map(r => [...r]) : newAttackGrid,
    hostShips: isHost ? gameState.hostShips : defendingShips,
    guestShips: isHost ? defendingShips : gameState.guestShips,
    hostShipsSunk: isHost ? newSunkShips : (gameState.hostShipsSunk || []),
    guestShipsSunk: isHost ? (gameState.guestShipsSunk || []) : newSunkShips,
    currentTurn: nextTurn,
    lastAttack: {
      row,
      col,
      result: hit ? 'hit' : 'miss',
      sunkShip: sunkShip ? sunkShip.name : null,
      attacker: attackingPlayerId,
    },
    isGameOver: allShipsSunk,
    winner: allShipsSunk ? attackingPlayerId : null,
  };

  return newGameState;
};

/**
 * Mark player as ready (ships placed)
 */
export const markPlayerReady = (gameState, playerId, ships) => {
  const isHost = playerId === gameState.hostId;

  const newGameState = {
    ...gameState,
    hostReady: isHost ? true : gameState.hostReady,
    guestReady: isHost ? gameState.guestReady : true,
    hostShips: isHost ? ships : (gameState.hostShips || []),
    guestShips: isHost ? (gameState.guestShips || []) : ships,
    // Ensure all required properties exist with defaults
    hostAttacks: gameState.hostAttacks || createEmptyGrid(),
    guestAttacks: gameState.guestAttacks || createEmptyGrid(),
    isGameOver: gameState.isGameOver || false,
    winner: gameState.winner || null,
    lastAttack: gameState.lastAttack || null,
    hostShipsSunk: gameState.hostShipsSunk || [],
    guestShipsSunk: gameState.guestShipsSunk || [],
  };

  // If both ready, start battle phase
  if (newGameState.hostReady && newGameState.guestReady) {
    newGameState.phase = 'battle';
  }

  return newGameState;
};

/**
 * Get ship data by ID
 */
export const getShipById = (shipId) => {
  return SHIPS.find(s => s.id === shipId);
};

/**
 * Export constants
 */
export { SHIPS, GRID_SIZE };
